import type { FC } from 'react';

import { Flex, Avatar, Tooltip, Typography, type FlexProps } from 'antd';
import styles from './channel.module.scss';
import classNames from 'classnames';
import { ConditionalWrapper } from '@shared/ui/conditional-wrapper';
// import { CheckListStatus } from '../check-list-status/check-list-status';
import type { ChannelTypeEnum } from '@shared/api';
import {
  Avito,
  ChannelWidget,
  CreatorMax,
  TelegramTypeLarge,
  VkTypeLarge,
} from '@shared/assets/icons/components';

export interface ChannelProps extends Omit<FlexProps, 'children'> {
  size?: 'sm' | 'md';
  type: ChannelTypeEnum;
  title?: string;
  caption?: string;
  maxTitleLength?: number;
  isTooltipVisible?: boolean;
  isActive?: boolean;
  showIcon?: boolean;
}

const MAX_CAPTION_LENGTH = 35;
const MAX_TITLE_LENGTH = 25;

export const Channel: FC<ChannelProps> = ({
  size = 'sm',
  type,
  title,
  caption,
  maxTitleLength = MAX_TITLE_LENGTH,
  isTooltipVisible = true,
  isActive = false,
  showIcon = true,
  ...flexProps
}) => {
  const { Text } = Typography;

  const hasLongTitle = title && title?.length > maxTitleLength;
  const titleSlice = hasLongTitle ? `${title.slice(0, maxTitleLength)}...` : title;

  const hasLongCaption = caption && caption?.length > MAX_CAPTION_LENGTH;
  const captionSlice = hasLongCaption ? `${caption.slice(0, MAX_CAPTION_LENGTH)}...` : caption;

  const isTelegramType = type === 'telegram' || type === 'telegram_bot';
  const isVKType = type === 'vk_bot';
  const isWidgetType = type === 'widget';
  const isMaxType = type === 'max_bot';
  const isAvitoType = type === 'avito';

  return (
    <Flex {...flexProps} align="center" className={styles.root} gap={showIcon ? '8px' : 0}>
      {showIcon && (
        <Flex className={styles.avatar_container} align="center" justify="center">
          <Avatar shape="square" className={classNames(styles.avatar_container, styles.avatar)}>
            {isTelegramType && <TelegramTypeLarge />}
            {isVKType && <VkTypeLarge />}
            {isWidgetType && <ChannelWidget width={60} height={60} />}
            {isMaxType && <CreatorMax width={60} height={60} />}
            {isAvitoType && <Avito width={60} height={60} />}
          </Avatar>
        </Flex>
      )}
      <Flex className={styles.container} vertical gap="2px">
        {title && (
          <Flex gap="4px" align="center">
            <Text className={styles.title}>
              <ConditionalWrapper
                condition={isTooltipVisible && (hasLongTitle || false)}
                wrapper={(children) => (
                  <Tooltip title={title} placement="top" className={styles.tooltip}>
                    {children}
                  </Tooltip>
                )}
              >
                <span>{titleSlice}</span>
              </ConditionalWrapper>
            </Text>

            {/* <CheckListStatus isActive={isActive} /> */}
          </Flex>
        )}

        {caption && (
          <Text className={styles.caption}>
            <ConditionalWrapper
              condition={isTooltipVisible && (hasLongCaption || false)}
              wrapper={(children) => (
                <Tooltip title={caption} placement="top" className={styles.tooltip}>
                  {children}
                </Tooltip>
              )}
            >
              <span>{captionSlice}</span>
            </ConditionalWrapper>
          </Text>
        )}
      </Flex>
    </Flex>
  );
};
